/**
 * Download modal component for displaying order download links.
 */

import { Modal, Button, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/apiClient';

interface DownloadModalProps {
  show: boolean;
  onHide: () => void;
  orderId: number;
}

interface DownloadFile {
  url: string;
  filename?: string;
  size?: string;
}

interface DownloadData {
  order_number?: string;
  files?: DownloadFile[];
}

export default function DownloadModal({ show, onHide, orderId }: DownloadModalProps) {
  const { data, isLoading, error } = useQuery<DownloadData>({
    queryKey: ['orderDownloadLinks', orderId],
    queryFn: async () => {
      const response = await apiClient.getOrderDownloadLinks(orderId);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as DownloadData;
    },
    enabled: show && !!orderId,
  });

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Download Files</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading download links...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">Error loading download links</Alert>
        ) : data && data.files ? (
          <div>
            <p>
              <strong>Order Number:</strong> {data.order_number || 'N/A'}
            </p>
            <h6>Download Links:</h6>
            <ListGroup>
              {Array.isArray(data.files) && data.files.length > 0 ? (
                data.files.map((file: DownloadFile, index: number) => (
                  <ListGroup.Item key={index}>
                    <a href={file.url} target="_blank" rel="noopener noreferrer" download>
                      {file.filename || `File ${index + 1}`}
                    </a>
                    {file.size && <span className="text-muted ms-2">({file.size})</span>}
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item>No files available for download</ListGroup.Item>
              )}
            </ListGroup>
          </div>
        ) : (
          <Alert variant="info">No download links available</Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
